import { useState, useCallback, useMemo, useEffect } from 'react'
import { Reorder, AnimatePresence, motion } from 'framer-motion'
import {
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronRight,
  FileJson,
  Download,
  FileText,
  Copy,
  Check,
  Tag,
  Layers,
} from 'lucide-react'
import FileUpload from '../components/FileUpload'
import ToggleSwitch from '../components/ToggleSwitch'
import CodeBlock from '../components/CodeBlock'
import { useToast } from '../components/Toast'
import { parsePreset, generatePresetSystemPrompt, getPromptsForDisplay } from '../utils/presetParser'
import type { ParsedPreset } from '../types'

interface DisplayPrompt {
  identifier: string
  name: string
  content: string
  role: string
  isMarker: boolean
  isEnabled: boolean
  isKnown: boolean
  id: string
}

const PRESET_TYPE_LABELS: Record<string, string> = {
  openai: 'OpenAI Preset',
  instruct: 'Instruct Template',
  context: 'Context Template',
  sysprompt: 'System Prompt',
  unknown: 'Unknown',
}

export default function PresetParser() {
  const { showToast } = useToast()
  const [prompts, setPrompts] = useState<DisplayPrompt[]>([])
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const [systemPrompt, setSystemPrompt] = useState('')
  const [originalPreset, setOriginalPreset] = useState<ParsedPreset | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!originalPreset) {
      setSystemPrompt('')
      return
    }
    const sp = generatePresetSystemPrompt(originalPreset)
    setSystemPrompt(sp)
  }, [originalPreset, prompts])

  const handleFileSelect = useCallback(
    async (file: File) => {
      try {
        const text = await file.text()
        const json = JSON.parse(text)
        const preset = parsePreset(json)

        const displayItems = getPromptsForDisplay(preset)
        const items: DisplayPrompt[] = displayItems.map((p, i) => ({
          ...p,
          id: p.identifier || `prompt_${i}_${Date.now()}`,
        }))

        setPrompts(items)
        setOriginalPreset(preset)
        setExpandedIds(new Set())
        showToast(`已识别为 ${PRESET_TYPE_LABELS[preset.preset_type]} 格式`, 'success')
      } catch (err) {
        const message = err instanceof Error ? err.message : '解析失败'
        showToast(`解析错误: ${message}`, 'error')
      }
    },
    [showToast]
  )

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const toggleEnabled = useCallback((id: string) => {
    setPrompts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, isEnabled: !p.isEnabled } : p))
    )
  }, [])

  const updateContent = useCallback((id: string, content: string) => {
    setPrompts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, content } : p))
    )
  }, [])

  const deletePrompt = useCallback((id: string) => {
    setPrompts((prev) => prev.filter((p) => p.id !== id))
    setExpandedIds((prev) => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }, [])

  const handleExportJson = useCallback(() => {
    if (!originalPreset) {
      showToast('没有可导出的预设数据', 'error')
      return
    }

    const exportData = {
      ...originalPreset,
      prompts: prompts.map(({ id: _id, isMarker: _m, isKnown: _k, isEnabled: _e, ...rest }) => rest),
      prompt_order: prompts.map((p) => ({
        identifier: p.identifier,
        enabled: p.isEnabled,
      })),
    }

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${originalPreset.name || 'preset'}_modified.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    showToast('JSON 已导出', 'success')
  }, [originalPreset, prompts, showToast])

  const handleCopyText = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(systemPrompt)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      showToast('已复制到剪贴板', 'success')
    } catch {
      showToast('复制失败', 'error')
    }
  }, [systemPrompt, showToast])

  const handleDownloadTxt = useCallback(() => {
    if (!systemPrompt) return

    const blob = new Blob([systemPrompt], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${originalPreset?.name || 'preset'}_system_prompt.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    showToast('文本已下载', 'success')
  }, [systemPrompt, originalPreset, showToast])

  const activeCount = useMemo(
    () => prompts.filter((p) => p.isEnabled && !p.isMarker).length,
    [prompts]
  )
  const markerCount = useMemo(
    () => prompts.filter((p) => p.isMarker).length,
    [prompts]
  )

  const hasContent = originalPreset && (prompts.length > 0 || originalPreset.context || originalPreset.instruct)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-fg tracking-tight">预设解析器</h1>
          <p className="text-sm text-muted mt-1">
            解析 SillyTavern 预设文件，自动生成系统提示
          </p>
        </div>
        {originalPreset && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-surface border border-stroke">
            <Tag size={14} className="text-accent" />
            <span className="text-sm text-muted">
              {PRESET_TYPE_LABELS[originalPreset.preset_type]}
            </span>
          </div>
        )}
      </div>

      <div className="ui-card p-6">
        <FileUpload
          accept=".json"
          onFileSelect={handleFileSelect}
          label="上传 SillyTavern 预设文件 (.json)"
        />
      </div>

      {/* Two-column: edit | live output */}
      {hasContent && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,460px)] gap-6 items-start">
          {/* Left column — edit */}
          <div className="space-y-6 order-2 lg:order-1">
            {/* Context Preset: story_string template */}
            {originalPreset?.context && (
              <div className="ui-card p-6 space-y-3">
                <h2 className="text-base font-semibold text-fg flex items-center gap-2 tracking-tight">
                  <Layers size={18} className="text-accent" />
                  Story String 模板
                </h2>
                <p className="text-xs text-faint">
                  此模板定义了角色卡字段如何组合为文本。变量: {'{{system}}'}, {'{{description}}'}, {'{{personality}}'}, {'{{scenario}}'}, {'{{wiBefore}}'}, {'{{wiAfter}}'}
                </p>
                <CodeBlock content={originalPreset.context.story_string} language="text" />
              </div>
            )}

            {/* Instruct Preset: template info */}
            {originalPreset?.instruct && (
              <div className="ui-card p-6 space-y-3">
                <h2 className="text-base font-semibold text-fg flex items-center gap-2 tracking-tight">
                  <Layers size={18} className="text-accent" />
                  Instruct 模板
                </h2>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {originalPreset.instruct.system_sequence && (
                    <div><span className="text-faint">System:</span> <code className="text-accent/80">{originalPreset.instruct.system_sequence}</code></div>
                  )}
                  {originalPreset.instruct.input_sequence && (
                    <div><span className="text-faint">User:</span> <code className="text-accent/80">{originalPreset.instruct.input_sequence}</code></div>
                  )}
                  {originalPreset.instruct.output_sequence && (
                    <div><span className="text-faint">Assistant:</span> <code className="text-accent/80">{originalPreset.instruct.output_sequence}</code></div>
                  )}
                  {originalPreset.instruct.stop_sequence && (
                    <div><span className="text-faint">Stop:</span> <code className="text-[#e0625f]/80">{originalPreset.instruct.stop_sequence}</code></div>
                  )}
                </div>
              </div>
            )}

            {/* Prompts List (OpenAI preset) */}
            {prompts.length > 0 && (
              <div className="ui-card p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-semibold text-fg tracking-tight">
                    Prompt 列表
                  </h2>
                  <span className="text-xs text-faint">
                    共 {prompts.length} 项 · 启用 {activeCount} 项 · 占位符 {markerCount} 项
                  </span>
                </div>

                <Reorder.Group
                  axis="y"
                  values={prompts}
                  onReorder={setPrompts}
                  className="space-y-2"
                >
                  <AnimatePresence initial={false}>
                    {prompts.map((prompt) => (
                      <Reorder.Item
                        key={prompt.id}
                        value={prompt}
                        className={`ui-card overflow-hidden ${
                          prompt.isMarker
                            ? 'border-dashed border-stroke bg-bg/50'
                            : ''
                        }`}
                      >
                        <div className="flex items-center gap-3 px-4 py-3">
                          <div
                            className="cursor-grab active:cursor-grabbing text-faint hover:text-fg transition-colors"
                            title="拖拽排序"
                          >
                            <GripVertical size={18} />
                          </div>

                          {!prompt.isMarker && (
                            <button
                              onClick={() => toggleExpanded(prompt.id)}
                              className="text-faint hover:text-fg transition-colors"
                              aria-label={expandedIds.has(prompt.id) ? '收起' : '展开'}
                            >
                              {expandedIds.has(prompt.id) ? (
                                <ChevronDown size={18} />
                              ) : (
                                <ChevronRight size={18} />
                              )}
                            </button>
                          )}
                          {prompt.isMarker && <div className="w-[18px]" />}

                          <span
                            className={`flex-1 text-sm font-medium truncate ${
                              prompt.isMarker
                                ? 'text-faint italic'
                                : prompt.isEnabled
                                  ? 'text-fg'
                                  : 'text-faint line-through'
                            }`}
                            title={`${prompt.name} (${prompt.identifier})`}
                          >
                            {prompt.name}
                            {prompt.isMarker && (
                              <span className="ml-2 text-xs text-faint">[占位符]</span>
                            )}
                          </span>

                          {!prompt.isMarker && (
                            <ToggleSwitch
                              checked={prompt.isEnabled}
                              onChange={() => toggleEnabled(prompt.id)}
                            />
                          )}

                          {!prompt.isMarker && (
                            <button
                              onClick={() => deletePrompt(prompt.id)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-faint hover:bg-raised hover:text-[#e0625f] transition-colors"
                              aria-label="删除"
                              title="删除"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>

                        <AnimatePresence>
                          {expandedIds.has(prompt.id) && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="px-4 pb-4 pt-1">
                                <textarea
                                  value={prompt.content}
                                  onChange={(e) => updateContent(prompt.id, e.target.value)}
                                  rows={6}
                                  className="ui-textarea leading-relaxed"
                                  placeholder="Prompt 内容..."
                                />
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </Reorder.Item>
                    ))}
                  </AnimatePresence>
                </Reorder.Group>

                <div className="flex flex-wrap items-center gap-3 pt-2">
                  <button
                    onClick={handleExportJson}
                    className="ui-btn px-4 py-2.5 rounded-md"
                  >
                    <FileJson size={18} />
                    导出 JSON
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right column — live output (sticky) */}
          <div className="space-y-4 order-1 lg:order-2 lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:pr-1">
            {prompts.length > 0 && (
              <div className="ui-card p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-semibold text-fg tracking-tight">
                    {originalPreset?.preset_type === 'openai' ? '系统提示输出' : '预设内容'}
                  </h2>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleCopyText}
                      disabled={!systemPrompt}
                      className="ui-btn px-3 py-1.5 rounded-md text-xs disabled:opacity-50 disabled:pointer-events-none"
                    >
                      {copied ? (
                        <><Check size={14} className="text-accent" /> 已复制</>
                      ) : (
                        <><Copy size={14} /> 复制</>
                      )}
                    </button>
                    <button
                      onClick={handleDownloadTxt}
                      disabled={!systemPrompt}
                      className="ui-btn px-3 py-1.5 rounded-md text-xs disabled:opacity-50 disabled:pointer-events-none"
                    >
                      <Download size={14} />
                      下载 .txt
                    </button>
                  </div>
                </div>

                {systemPrompt ? (
                  <CodeBlock
                    content={systemPrompt}
                    language="text"
                    showLineNumbers={false}
                  />
                ) : (
                  <div className="rounded-md bg-bg border border-stroke py-12 text-center text-faint text-sm">
                    <FileText size={32} className="mx-auto mb-3 text-faint" />
                    {originalPreset?.preset_type === 'openai'
                      ? '没有可输出的 prompt 内容（所有非占位符 prompt 均已禁用或为空）'
                      : '此预设类型不直接生成系统提示'}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
